import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './Header.css';
import logoFinal from '../../assets/logo_final.png';
// ── SVG Icons ──────────────────────────────────────────────
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 1h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const MapPinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const ChevronDown = () => (
  <svg className="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

// ── Nav Data ────────────────────────────────────────────────
const NAV_ITEMS = [
  { label: 'Home', href: '/', type: 'route' },
  {
    label: 'Services',
    href: '/#services',
    type: 'hash',
    dropdown: [
      { label: 'Soil Investigation',          href: '/services/soil-investigation',  type: 'route' },
      { label: 'Recharge / Relief Well',       href: '/services/recharge-well',       type: 'route' },
      { label: 'Pumping Test',                 href: '/services/pumping-test',        type: 'route' },
      { label: 'TAM Grouting',                 href: '/services/tam-grouting',        type: 'route' },
      { label: 'Fissure Grouting',             href: '/services/fissure-grouting',    type: 'route' },
      { label: 'JET Grouting',                 href: '/services/jet-grouting',        type: 'route' },
      { label: 'Instrumentation & Monitoring', href: '/services/instrumentation',     type: 'route' },
      //{ label: 'Shaft & Pipe Jacking',         href: '/services/shaft-pipe',          type: 'route' },
    ],
  },
  { label: 'AboutUs',   href: '/about-us',   type: 'route' },
  { label: 'Projects',  href: '/projects',  type: 'route' },
  { label: 'ContactUs', href: '/contact-us', type: 'route' },
];

// ── Logo ────────────────────────────────────────────────────
const LOGO_IMG = logoFinal;

// ── Helper: figure out which nav label matches the current URL ──
const getActiveLinkFromPath = (pathname) => {
  // Exact match on a top-level route (Home, AboutUs, Projects, ContactUs)
  const exactMatch = NAV_ITEMS.find((item) => item.type === 'route' && item.href === pathname);
  if (exactMatch) return exactMatch.label;

  // Check dropdown sub-routes (e.g. /services/soil-investigation → "Services")
  const parentMatch = NAV_ITEMS.find(
    (item) => item.dropdown && item.dropdown.some((sub) => sub.href === pathname)
  );
  if (parentMatch) return parentMatch.label;

  return null;
};

// ── Header Component ────────────────────────────────────────
const Header = () => {
  const [scrolled, setScrolled]         = useState(false);
  const [menuOpen, setMenuOpen]         = useState(false);
  const [activeLink, setActiveLink]     = useState(() => getActiveLinkFromPath(window.location.pathname) || 'Home');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen]     = useState(null);
  const [menuTop, setMenuTop]           = useState(64);

  const headerRef = useRef(null);

  // ── Scroll detection ──
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── Track header height to position portal menu correctly ──
  useEffect(() => {
    const updateTop = () => {
      if (headerRef.current) {
        setMenuTop(headerRef.current.offsetHeight);
      }
    };
    updateTop();
    window.addEventListener('scroll', updateTop, { passive: true });
    window.addEventListener('resize', updateTop);
    return () => {
      window.removeEventListener('scroll', updateTop);
      window.removeEventListener('resize', updateTop);
    };
  }, [scrolled]);

  // ── Keep active nav link in sync with the current URL ──
  useEffect(() => {
    const syncActiveLink = () => {
      const match = getActiveLinkFromPath(window.location.pathname);
      if (match) setActiveLink(match);
    };
    syncActiveLink();
    window.addEventListener('popstate', syncActiveLink);
    return () => window.removeEventListener('popstate', syncActiveLink);
  }, []);

  // ── Prevent body scroll when mobile menu open ──
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // ── Close desktop dropdown on outside click ──
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleNavClick = (label) => {
    setActiveLink(label);
    setMenuOpen(false);
    setMobileOpen(null);
  };

  const handleDesktopDropdownToggle = (label, hasDropdown, e) => {
    if (hasDropdown) {
      e.preventDefault();
      setOpenDropdown(openDropdown === label ? null : label);
    } else {
      setActiveLink(label);
      setOpenDropdown(null);
    }
  };

  const handleMobileLinkClick = (e, item) => {
    if (item.dropdown) {
      e.preventDefault();
      setMobileOpen(mobileOpen === item.label ? null : item.label);
    } else {
      handleNavClick(item.label);
    }
  };

  // ── Mobile Menu rendered via Portal into document.body ──
  // This escapes the header's stacking context so it can truly cover the full screen
  const mobileMenu = ReactDOM.createPortal(
    <div
      className={`gsk-header__mobile-menu ${menuOpen ? 'open' : ''}`}
      style={{ top: `${menuTop}px` }}
    >
      {NAV_ITEMS.map((item, i) => (
        <React.Fragment key={item.label}>
          <a
            href={item.href}
            className={`gsk-header__mobile-link ${activeLink === item.label ? 'active' : ''} ${mobileOpen === item.label ? 'accordion-open' : ''}`}
            onClick={(e) => handleMobileLinkClick(e, item)}
          >
            {item.label}
            {item.dropdown && (
              <span className={`gsk-header__mobile-chevron ${mobileOpen === item.label ? 'rotated' : ''}`}>
                <ChevronDown />
              </span>
            )}
          </a>

          {item.dropdown && (
            <div className={`gsk-header__mobile-submenu ${mobileOpen === item.label ? 'open' : ''}`}>
              <div>
                {item.dropdown.map((sub) => (
                  <a
                    key={sub.label}
                    href={sub.href}
                    className="gsk-header__mobile-sublink"
                    onClick={() => handleNavClick(item.label)}
                  >
                    {sub.label}
                  </a>
                ))}
              </div>
            </div>
          )}

          {i < NAV_ITEMS.length - 1 && <div className="gsk-header__mobile-divider" />}
        </React.Fragment>
      ))}

      <a
        href="/contact-us"
        className="gsk-header__mobile-cta"
        onClick={() => { setMenuOpen(false); setMobileOpen(null); }}
      >
        Contact Us <ArrowRight />
      </a>

      <div className="gsk-header__mobile-contact">
        <a href="tel:+6592398494" className="gsk-header__mobile-contact-item">
          <PhoneIcon /> +65 9239 8494 (Nedu)
        </a>
        <a href="tel:+6590271160" className="gsk-header__mobile-contact-item">
          <PhoneIcon /> +65 9027 1160 (Suben)
        </a>
        <a href="mailto:info@gskgeotech.com" className="gsk-header__mobile-contact-item">
          <MailIcon /> info@gskgeotech.com
        </a>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <header ref={headerRef} className={`gsk-header ${scrolled ? 'scrolled' : ''}`}>

        {/* ── Top Bar ── */}
        <div className="gsk-header__topbar">
          <a href="tel:+6592398494" className="gsk-header__topbar-item">
            <PhoneIcon /> +65 9239 8494
          </a>
          <div className="gsk-header__topbar-divider" />
          <a href="mailto:info@gskgeotech.com" className="gsk-header__topbar-item">
            <MailIcon /> info@gskgeotech.com
          </a>
          <div className="gsk-header__topbar-divider" />
          <span className="gsk-header__topbar-item">
            <MapPinIcon /> 19 Veersamy Road, Singapore 207350
          </span>
        </div>

        {/* ── Main Nav ── */}
        <div className="gsk-header__main">

          {/* Logo */}
          <a href="/" className="gsk-header__logo" onClick={() => handleNavClick('Home')}>
            {LOGO_IMG ? (
              <img src={LOGO_IMG} alt="GSK Geotech" className="gsk-header__logo-img" />
            ) : (
              <>
                <div className="gsk-header__logo-icon">GSK</div>
                <div className="gsk-header__logo-text">
                  <span className="gsk-header__logo-name">GSK Geotech</span>
                  <span className="gsk-header__logo-tagline">Pte Ltd · Singapore</span>
                </div>
              </>
            )}
          </a>

          {/* Desktop Nav */}
          <nav>
            <ul className="gsk-header__nav">
              {NAV_ITEMS.map((item) => (
                <li
                  key={item.label}
                  className={`gsk-header__nav-item ${openDropdown === item.label ? 'dropdown-open' : ''}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <a
                    href={item.href}
                    className={`gsk-header__nav-link ${activeLink === item.label ? 'active' : ''}`}
                    onClick={(e) => handleDesktopDropdownToggle(item.label, !!item.dropdown, e)}
                  >
                    {item.label}
                    {item.dropdown && <ChevronDown />}
                  </a>

                  {item.dropdown && (
                    <div className="gsk-header__dropdown">
                      {item.dropdown.map((sub) => (
                        <a
                          key={sub.label}
                          href={sub.href}
                          className="gsk-header__dropdown-item"
                          onClick={() => { handleNavClick(item.label); setOpenDropdown(null); }}
                        >
                          {sub.label}
                        </a>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* CTA */}
          <a href="/contact-us" className="gsk-header__cta" onClick={() => handleNavClick('ContactUs')}>
            Contact Us <ArrowRight />
          </a>

          {/* Hamburger */}
          <button
            className={`gsk-header__hamburger ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>

      </header>

      {/* Portal menu renders here, directly in document.body */}
      {mobileMenu}
    </>
  );
};

export default Header;